import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import * as XLSX from 'xlsx'
import {
  CCard,
  CCardBody,
  CRow,
  CCol,
  CButton,
  CLoadingButton,
  CFormInput,
  CFormSelect,
  CAlert,
  CTable,
  CTableHead,
  CTableRow,
  CTableHeaderCell,
  CTableBody,
  CTableDataCell,
  CSpinner,
  CBadge,
} from '@coreui/react-pro'
import CIcon from '@coreui/icons-react'
import {
  cilArrowLeft,
  cilCheck,
  cilCheckAlt,
  cilCheckCircle,
  cilCloudDownload,
  cilCloudUpload,
  cilFile,
  cilInfo,
  cilWarning,
  cilX,
} from '@coreui/icons'
import ToastComponent from 'src/components/common/TaostComponent'
import PlayerService from 'src/service/PlayerService'
import ExampleCsv from '../../assets/PlayerSampleFile.csv'

const WIZARD_STEPS = ['Upload File', 'Validate Players', 'Review & Import']
const FALLBACK_POSITIONS = ['BAT', 'BOWL', 'AR', 'WK']
const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10MB

// Solid badge colors (not the bg-*-subtle/text-*-emphasis utilities) so the label always
// renders with guaranteed contrast, plus an accent used to tint/border each table row.
const RESULT_META = {
  valid: { label: 'Valid', color: 'success', icon: cilCheckCircle, accent: '#198754' },
  missing_first_name: {
    label: 'Missing First Name',
    color: 'danger',
    icon: cilWarning,
    accent: '#dc3545',
  },
  missing_last_name: {
    label: 'Missing Last Name',
    color: 'danger',
    icon: cilWarning,
    accent: '#dc3545',
  },
  missing_position: {
    label: 'Missing Position',
    color: 'danger',
    icon: cilWarning,
    accent: '#dc3545',
  },
  missing_value: { label: 'Missing Value', color: 'danger', icon: cilWarning, accent: '#dc3545' },
  invalid_position: {
    label: 'Invalid Position',
    color: 'warning',
    icon: cilWarning,
    accent: '#ffc107',
  },
  invalid_value: { label: 'Invalid Value', color: 'warning', icon: cilWarning, accent: '#ffc107' },
  invalid_team_code: {
    label: 'Invalid Team Code',
    color: 'info',
    icon: cilWarning,
    accent: '#0dcaf0',
  },
}

const tintStyle = (hex, alphaHex) => ({ backgroundColor: `${hex}${alphaHex}` })

const guessColumn = (headers, patterns) =>
  headers.find((h) =>
    patterns.some((p) =>
      String(h)
        .toLowerCase()
        .replace(/[^a-z]/g, '')
        .includes(p),
    ),
  ) || ''

// Lets users enter a plain number ("9" or "9.00") for Value instead of the "$9.00m" display
// format - strips everything but digits/decimal point, then keys off the numeric amount.
const parseValueNumber = (v) => {
  if (v === null || v === undefined) return null
  const cleaned = String(v).replace(/[^0-9.]/g, '')
  if (!cleaned) return null
  const num = parseFloat(cleaned)
  return Number.isNaN(num) ? null : num
}

// Matches the backend's hardcoded $4.00m-$90.00m (step $0.50) value list, used only as a
// fallback if this game's own price tiers can't be fetched.
const genFullValueRange = () => {
  const out = []
  for (let cents = 400; cents <= 9000; cents += 50) {
    out.push(`$${(cents / 100).toFixed(2)}m`)
  }
  return out.reverse()
}

const MiniStepper = ({ current, maxReached, onStepClick }) => (
  <div className="d-flex align-items-center mb-4 flex-wrap">
    {WIZARD_STEPS.map((label, idx) => {
      const n = idx + 1
      const done = n < current
      const active = n === current
      const clickable = n <= maxReached
      return (
        <React.Fragment key={label}>
          <div
            className="d-flex align-items-center gap-2"
            style={{ cursor: clickable ? 'pointer' : 'default', opacity: clickable ? 1 : 0.5 }}
            onClick={() => clickable && onStepClick(n)}
          >
            <div
              className={`d-flex align-items-center justify-content-center rounded-circle flex-shrink-0 ${
                done ? 'bg-success text-white' : active ? 'bg-primary text-white' : 'bg-body border'
              }`}
              style={{ width: 28, height: 28, fontSize: 13 }}
            >
              {done ? <CIcon icon={cilCheckAlt} size="sm" /> : n}
            </div>
            <span className={`small ${active ? 'fw-semibold text-primary' : ''}`}>{label}</span>
          </div>
          {n < WIZARD_STEPS.length && (
            <div
              className={`mx-2 ${done ? 'bg-success' : 'bg-secondary-subtle'}`}
              style={{ height: 2, width: 32 }}
            />
          )}
        </React.Fragment>
      )
    })}
  </div>
)

const InfoBox = ({ children, color = 'info' }) => (
  <CAlert color={color} className="d-flex align-items-start gap-2 border-0">
    <CIcon icon={cilInfo} className="flex-shrink-0 mt-1" />
    <div>{children}</div>
  </CAlert>
)

// Explicit hex (not the bg-*-subtle/text-*-emphasis utilities, which render invisible text in
// this theme) so the tinted background, icon chip and number all keep guaranteed contrast.
const TONE_HEX = {
  success: '#198754',
  danger: '#dc3545',
  warning: '#b45309',
  info: '#087990',
  secondary: '#495057',
}

const StatTile = ({ label, value, tone, icon }) => {
  const hex = TONE_HEX[tone] || TONE_HEX.secondary
  return (
    <div
      className="rounded-4 p-3 flex-fill d-flex align-items-center gap-3"
      style={{ ...tintStyle(hex, '14'), border: `1px solid ${hex}33` }}
    >
      <div
        className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
        style={{ width: 38, height: 38, backgroundColor: hex, color: '#fff' }}
      >
        <CIcon icon={icon} size="sm" />
      </div>
      <div>
        <div className="fs-4 fw-bold lh-1" style={{ color: hex }}>
          {value}
        </div>
        <div className="small text-medium-emphasis">{label}</div>
      </div>
    </div>
  )
}

// Player Import: upload a CSV/Excel file, validate each row against this game's actual
// position/value/team-code options (fetched, not hardcoded), fix any of it inline, then
// commit only the rows left included. Mirrors the "Import List" guest wizard's 3-step shape
// (Upload File -> Validate -> Review & Import), adapted for player fields instead of guests.
const ImportPlayer = () => {
  const navigate = useNavigate()
  const fileInputRef = useRef(null)

  const [wizardStep, setWizardStep] = useState(1)
  const [maxWizardStep, setMaxWizardStep] = useState(1)

  const [fileName, setFileName] = useState('')
  const [fileRowCount, setFileRowCount] = useState(0)
  const [busy, setBusy] = useState(false)
  const [isDragging, setIsDragging] = useState(false)

  const [optionsLoading, setOptionsLoading] = useState(true)
  const [positions, setPositions] = useState(FALLBACK_POSITIONS.map((p) => ({ id: p, name: p })))
  const [teams, setTeams] = useState([]) // { id, name, sn }
  const [values, setValues] = useState([]) // price_name strings, e.g. "$6.00m"

  const [rows, setRows] = useState([]) // { row, first_name, last_name, position, value, team_code, team_name, result, included }
  const [editingRow, setEditingRow] = useState(null)
  const [editDraft, setEditDraft] = useState({
    first_name: '',
    last_name: '',
    position: '',
    value: '',
    team_code: '',
  })

  const [importing, setImporting] = useState(false)
  const [importErrors, setImportErrors] = useState([])

  useEffect(() => {
    PlayerService.getTeamPositionValueBatBowlStyle()
      .then((res) => {
        if (res?.position?.length) setPositions(res.position)
        if (res?.teamList) setTeams(res.teamList)
        const priceNames = (res?.fantasy_values || []).map((v) => v.price_name).filter(Boolean)
        setValues(priceNames.length ? priceNames : genFullValueRange())
      })
      .catch(() => setValues(genFullValueRange()))
      .finally(() => setOptionsLoading(false))
  }, [])

  const validPositionSet = useMemo(
    () => new Set(positions.map((p) => String(p.name).toUpperCase())),
    [positions],
  )
  // Maps the numeric amount (e.g. "9.00") to the canonical "$9.00m" price tier, so a user can
  // type 9, 9.0 or $9.00m in the Value column and all resolve to the same tier.
  const valueByNumber = useMemo(() => {
    const map = new Map()
    values.forEach((v) => {
      const num = parseValueNumber(v)
      if (num !== null) map.set(num.toFixed(2), v)
    })
    return map
  }, [values])
  const teamBySn = useMemo(() => {
    const map = new Map()
    teams.forEach((t) => t.sn && map.set(String(t.sn).toUpperCase(), t))
    return map
  }, [teams])

  const validateRow = (r) => {
    if (!r.first_name) return 'missing_first_name'
    if (!r.last_name) return 'missing_last_name'
    if (!r.position) return 'missing_position'
    if (!r.value) return 'missing_value'
    if (!validPositionSet.has(r.position.toUpperCase())) return 'invalid_position'
    const valueNum = parseValueNumber(r.value)
    if (valueNum === null || !valueByNumber.has(valueNum.toFixed(2))) return 'invalid_value'
    if (r.team_code && !teamBySn.has(r.team_code.toUpperCase())) return 'invalid_team_code'
    return 'valid'
  }

  // Snaps a row's position/value/team_code to the canonical option it matched (case, exact
  // "$X.XXm" formatting) so what gets sent to the backend always matches its expected format.
  const normalizeRow = (r) => {
    const positionMatch = positions.find(
      (p) => String(p.name).toUpperCase() === r.position?.toUpperCase(),
    )
    const valueNum = parseValueNumber(r.value)
    const valueMatch = valueNum !== null ? valueByNumber.get(valueNum.toFixed(2)) : null
    const teamMatch = r.team_code ? teamBySn.get(r.team_code.toUpperCase()) : null
    return {
      ...r,
      position: positionMatch ? positionMatch.name : r.position,
      value: valueMatch || r.value,
      team_code: teamMatch ? teamMatch.sn : r.team_code,
      team_name: teamMatch ? teamMatch.name : '',
    }
  }

  const goToStep = (n) => {
    setWizardStep(n)
    setMaxWizardStep((m) => Math.max(m, n))
  }

  const resetAll = () => {
    setWizardStep(1)
    setMaxWizardStep(1)
    setFileName('')
    setFileRowCount(0)
    setRows([])
    setImportErrors([])
  }

  const handleFile = (file) => {
    if (!file) return
    if (file.size > MAX_FILE_SIZE) {
      ToastComponent('File is too large. Maximum size is 10MB.', 'error')
      return
    }
    setBusy(true)
    const reader = new FileReader()
    reader.onload = (evt) => {
      let json = []
      let rawHeaders = []
      try {
        const isCsv = /\.csv$/i.test(file.name)
        const workbook = XLSX.read(evt.target.result, { type: isCsv ? 'string' : 'array' })
        const sheet = workbook.Sheets[workbook.SheetNames[0]]
        json = XLSX.utils.sheet_to_json(sheet, { defval: '' })
        rawHeaders = json.length ? Object.keys(json[0]) : []
      } catch (err) {
        ToastComponent('Could not read this file. Please check the format and try again.', 'error')
        setBusy(false)
        return
      }

      if (!rawHeaders.length) {
        ToastComponent('This file has no rows to import.', 'error')
        setBusy(false)
        return
      }

      const detected = {
        first_name: guessColumn(rawHeaders, ['firstname', 'first', 'fname']),
        last_name: guessColumn(rawHeaders, ['lastname', 'last', 'lname']),
        position: guessColumn(rawHeaders, ['position', 'pos']),
        value: guessColumn(rawHeaders, ['value', 'price']),
        team_code: guessColumn(rawHeaders, ['teamcode', 'team', 'sn']),
      }

      const nextRows = json.map((r, idx) => {
        const base = {
          row: idx + 1,
          first_name: detected.first_name ? String(r[detected.first_name] ?? '').trim() : '',
          last_name: detected.last_name ? String(r[detected.last_name] ?? '').trim() : '',
          position: detected.position ? String(r[detected.position] ?? '').trim() : '',
          value: detected.value ? String(r[detected.value] ?? '').trim() : '',
          team_code: detected.team_code ? String(r[detected.team_code] ?? '').trim() : '',
        }
        const normalized = normalizeRow(base)
        const result = validateRow(normalized)
        return { ...normalized, result, included: result === 'valid' }
      })

      setFileName(file.name)
      setFileRowCount(json.length)
      setRows(nextRows)
      setBusy(false)
    }
    if (/\.csv$/i.test(file.name)) reader.readAsText(file)
    else reader.readAsArrayBuffer(file)
  }

  const handleDrop = (e) => {
    e.preventDefault()
    setIsDragging(false)
    handleFile(e.dataTransfer.files?.[0])
  }

  const toggleRowIncluded = (row) => {
    setRows((prev) =>
      prev.map((r) =>
        r.row === row && r.result === 'valid' ? { ...r, included: !r.included } : r,
      ),
    )
  }

  const startEdit = (r) => {
    setEditingRow(r.row)
    setEditDraft({
      first_name: r.first_name,
      last_name: r.last_name,
      position: r.position,
      value: r.value,
      team_code: r.team_code,
    })
  }

  const cancelEdit = () => {
    setEditingRow(null)
    setEditDraft({ first_name: '', last_name: '', position: '', value: '', team_code: '' })
  }

  const saveEdit = (row) => {
    const draft = {
      row,
      first_name: editDraft.first_name.trim(),
      last_name: editDraft.last_name.trim(),
      position: editDraft.position.trim(),
      value: editDraft.value.trim(),
      team_code: editDraft.team_code.trim(),
    }
    const normalized = normalizeRow(draft)
    const result = validateRow(normalized)
    setRows((prev) =>
      prev.map((r) =>
        r.row === row ? { ...normalized, result, included: result === 'valid' } : r,
      ),
    )
    cancelEdit()
  }

  const summary = useMemo(() => {
    const valid = rows.filter((r) => r.result === 'valid').length
    return { total: rows.length, valid, needsAttention: rows.length - valid }
  }, [rows])

  const readyRows = rows.filter((r) => r.included)

  const runImport = () => {
    if (!readyRows.length) return
    setImporting(true)
    setImportErrors([])
    const playerData = readyRows.map((r) => ({
      first_name: r.first_name,
      last_name: r.last_name,
      position: r.position,
      value: r.value,
      team_code: r.team_code || '',
    }))
    PlayerService.playerImport({ playerData })
      .then((res) => {
        if (res.success) {
          ToastComponent(res.message || 'Players imported successfully.', 'success')
          resetAll()
          navigate('/players')
        } else {
          setImportErrors(res.errors || ['An unknown error occurred.'])
          ToastComponent('Import failed. Please check the errors below.', 'error')
        }
      })
      .catch(() => ToastComponent('Import failed. Please try again.', 'error'))
      .finally(() => setImporting(false))
  }

  const positionNames = positions.map((p) => p.name).join(', ')
  const valueRange = values.length
    ? `${parseValueNumber(values[values.length - 1])} – ${parseValueNumber(values[0])}`
    : ''
  const sampleValueNumber = values.length ? parseValueNumber(values[0]) : 6

  const renderUpload = () => (
    <>
      <CRow className="g-4">
        <CCol lg={5}>
          <CCard className="border-secondary-subtle rounded-4 h-100">
            <CCardBody>
              <div className="fw-semibold fs-6 mb-1">Download Template</div>
              <div className="small mb-3">Use the template to prepare your player records.</div>
              <a
                href={ExampleCsv}
                download="Player-Sample-CSV.csv"
                target="_blank"
                rel="noreferrer"
              >
                <CButton color="primary" variant="outline" className="mb-4">
                  <CIcon icon={cilCloudDownload} className="me-1" />
                  Download Player Template
                </CButton>
              </a>
              <div className="fw-semibold mb-2">Required columns</div>
              <ul className="ps-3 mb-3 small">
                <li>First Name</li>
                <li>Last Name</li>
                <li>
                  Position{' '}
                  <span className="text-medium-emphasis">
                    ({positionNames || 'BAT, BOWL, AR, WK'})
                  </span>
                </li>
                <li>
                  Value{' '}
                  <span className="text-medium-emphasis">{valueRange && `(${valueRange})`}</span>
                </li>
                <li>
                  Team Code <span className="text-medium-emphasis">(optional)</span>
                </li>
              </ul>
              <InfoBox color="light">
                Enter the Value as a plain number matching one of this game&rsquo;s configured price
                tiers &mdash; no <strong>$</strong> or <strong>m</strong> (e.g. {sampleValueNumber}
                ). Team Code must match an existing team&rsquo;s code.
              </InfoBox>
            </CCardBody>
          </CCard>
        </CCol>

        <CCol lg={7}>
          <CCard className="border-secondary-subtle rounded-4 h-100">
            <CCardBody>
              <div className="fw-semibold fs-6 mb-1">Upload Completed Spreadsheet</div>
              <div className="small mb-3">
                Drag and drop an XLSX or CSV file, or browse your computer.
              </div>

              {!fileName ? (
                <div
                  className="border rounded-4 d-flex flex-column align-items-center justify-content-center text-center p-4"
                  style={{
                    cursor: optionsLoading ? 'not-allowed' : 'pointer',
                    minHeight: 180,
                    backgroundColor: isDragging ? 'rgba(var(--cui-primary-rgb), 0.06)' : undefined,
                    borderStyle: 'dashed',
                    opacity: optionsLoading ? 0.6 : 1,
                  }}
                  onClick={() => !optionsLoading && fileInputRef.current?.click()}
                  onDragOver={(e) => {
                    e.preventDefault()
                    if (!optionsLoading) setIsDragging(true)
                  }}
                  onDragLeave={() => setIsDragging(false)}
                  onDrop={(e) => !optionsLoading && handleDrop(e)}
                >
                  <CIcon icon={cilCloudUpload} size="xl" className="text-primary mb-2" />
                  <div>
                    Drag and drop an XLSX or CSV file, or{' '}
                    <span className="text-primary">browse</span>
                  </div>
                  <div className="text-medium-emphasis small mt-1">Maximum file size 10MB</div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    hidden
                    accept=".csv,.xlsx,.xls"
                    disabled={optionsLoading}
                    onChange={(e) => {
                      handleFile(e.target.files?.[0])
                      e.target.value = ''
                    }}
                  />
                </div>
              ) : (
                <CCard className="border-secondary-subtle rounded-3">
                  <CCardBody className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                    <div className="d-flex align-items-center gap-2">
                      <CIcon icon={cilFile} className="text-success" size="xl" />
                      <div>
                        <div className="fw-semibold">{fileName}</div>
                        <div className="small text-success d-flex align-items-center gap-1">
                          <CIcon icon={cilCheckCircle} size="sm" />
                          {fileRowCount} row{fileRowCount === 1 ? '' : 's'} detected
                        </div>
                      </div>
                    </div>
                    <CButton color="link" className="text-danger p-0" onClick={resetAll}>
                      Remove
                    </CButton>
                  </CCardBody>
                </CCard>
              )}

              {(busy || optionsLoading) && (
                <div className="d-flex align-items-center gap-2 small text-medium-emphasis mt-2">
                  <CSpinner size="sm" />
                  {optionsLoading
                    ? 'Loading position, value and team data for this game…'
                    : 'Reading and validating your file…'}
                </div>
              )}

              <div className="text-medium-emphasis small mt-2">
                <a
                  href={ExampleCsv}
                  download="Player-Sample-CSV.csv"
                  target="_blank"
                  rel="noreferrer"
                  className="text-medium-emphasis text-decoration-none"
                >
                  <CIcon icon={cilCloudDownload} size="sm" className="me-1" />
                  Download Sample CSV
                </a>
              </div>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>
      <div className="d-flex justify-content-between mt-4">
        <CButton color="secondary" variant="outline" onClick={() => navigate('/players')}>
          Cancel
        </CButton>
        <CButton color="primary" disabled={!rows.length} onClick={() => goToStep(2)}>
          Review File
        </CButton>
      </div>
    </>
  )

  const renderValidate = () => (
    <>
      <CCard className="border-secondary-subtle rounded-4 mb-3">
        <CCardBody className="d-flex align-items-center justify-content-between flex-wrap gap-2">
          <div className="d-flex align-items-center gap-2">
            <CIcon icon={cilFile} className="text-primary" />
            <div>
              <div className="fw-semibold">{fileName}</div>
              <div className="small text-medium-emphasis">{fileRowCount} rows</div>
            </div>
          </div>
          <CButton
            color="secondary"
            variant="outline"
            size="sm"
            onClick={() => {
              resetAll()
            }}
          >
            Replace File
          </CButton>
        </CCardBody>
      </CCard>

      <div className="d-flex gap-3 flex-wrap mb-3">
        <StatTile label="Valid" value={summary.valid} tone="success" icon={cilCheckCircle} />
        <StatTile
          label="Needs Attention"
          value={summary.needsAttention}
          tone="warning"
          icon={cilWarning}
        />
      </div>

      <CCard className="border-secondary-subtle rounded-4 overflow-hidden">
        <div className="table-responsive">
          <CTable align="middle" hover className="mb-0 pi-import-table">
            <CTableHead style={{ backgroundColor: 'var(--cui-tertiary-bg, #f8f9fa)' }}>
              <CTableRow>
                <CTableHeaderCell className="ps-4">Row</CTableHeaderCell>
                <CTableHeaderCell>First Name</CTableHeaderCell>
                <CTableHeaderCell>Last Name</CTableHeaderCell>
                <CTableHeaderCell>Position</CTableHeaderCell>
                <CTableHeaderCell>Value</CTableHeaderCell>
                <CTableHeaderCell>Team</CTableHeaderCell>
                <CTableHeaderCell>Result</CTableHeaderCell>
                <CTableHeaderCell>Action</CTableHeaderCell>
              </CTableRow>
            </CTableHead>
            <CTableBody>
              {rows.map((r) => {
                const meta = RESULT_META[r.result] || RESULT_META.valid
                const isEditing = editingRow === r.row
                return (
                  <CTableRow
                    key={r.row}
                    // className={r.included ? '' : 'opacity-50'}
                    style={{
                      ...tintStyle(meta.accent, '0d'),
                      borderLeft: `3px solid ${meta.accent}`,
                    }}
                  >
                    <CTableDataCell className="ps-4">{r.row}</CTableDataCell>
                    <CTableDataCell>
                      {isEditing ? (
                        <CFormInput
                          size="sm"
                          value={editDraft.first_name}
                          onChange={(e) =>
                            setEditDraft((d) => ({ ...d, first_name: e.target.value }))
                          }
                        />
                      ) : (
                        r.first_name || '—'
                      )}
                    </CTableDataCell>
                    <CTableDataCell>
                      {isEditing ? (
                        <CFormInput
                          size="sm"
                          value={editDraft.last_name}
                          onChange={(e) =>
                            setEditDraft((d) => ({ ...d, last_name: e.target.value }))
                          }
                        />
                      ) : (
                        r.last_name || '—'
                      )}
                    </CTableDataCell>
                    <CTableDataCell>
                      {isEditing ? (
                        <CFormSelect
                          size="sm"
                          value={editDraft.position}
                          onChange={(e) =>
                            setEditDraft((d) => ({ ...d, position: e.target.value }))
                          }
                        >
                          <option value="">Select Position</option>
                          {positions.map((p) => (
                            <option value={p.name} key={p.id}>
                              {p.name}
                            </option>
                          ))}
                        </CFormSelect>
                      ) : (
                        r.position || '—'
                      )}
                    </CTableDataCell>
                    <CTableDataCell>
                      {isEditing ? (
                        <CFormSelect
                          size="sm"
                          value={editDraft.value}
                          onChange={(e) => setEditDraft((d) => ({ ...d, value: e.target.value }))}
                        >
                          <option value="">Select Value</option>
                          {values.map((v) => (
                            <option value={v} key={v}>
                              {v}
                            </option>
                          ))}
                        </CFormSelect>
                      ) : (
                        r.value || '—'
                      )}
                    </CTableDataCell>
                    <CTableDataCell>
                      {isEditing ? (
                        <CFormSelect
                          size="sm"
                          value={editDraft.team_code}
                          onChange={(e) =>
                            setEditDraft((d) => ({ ...d, team_code: e.target.value }))
                          }
                        >
                          <option value="">No Team</option>
                          {teams.map((t) => (
                            <option value={t.sn} key={t.id}>
                              {t.name} ({t.sn})
                            </option>
                          ))}
                        </CFormSelect>
                      ) : r.team_code ? (
                        `${r.team_name || r.team_code}${r.team_name ? ` (${r.team_code})` : ''}`
                      ) : (
                        '—'
                      )}
                    </CTableDataCell>
                    <CTableDataCell>
                      <CBadge
                        color={meta.color}
                        shape="rounded-pill"
                        className="d-inline-flex align-items-center gap-1 px-2 py-1"
                      >
                        <CIcon icon={meta.icon} size="sm" />
                        {meta.label}
                      </CBadge>
                    </CTableDataCell>
                    <CTableDataCell>
                      {isEditing ? (
                        <div className="d-flex gap-1">
                          <CButton size="sm" color="primary" onClick={() => saveEdit(r.row)}>
                            <CIcon icon={cilCheck} />
                          </CButton>
                          <CButton
                            size="sm"
                            color="secondary"
                            variant="outline"
                            onClick={cancelEdit}
                          >
                            <CIcon icon={cilX} />
                          </CButton>
                        </div>
                      ) : (
                        <div className="d-flex gap-2">
                          <CButton
                            size="sm"
                            color="link"
                            className="p-0"
                            onClick={() => startEdit(r)}
                          >
                            Edit
                          </CButton>
                          {r.result === 'valid' && (
                            <CButton
                              size="sm"
                              color="link"
                              className="p-0"
                              onClick={() => toggleRowIncluded(r.row)}
                            >
                              {r.included ? 'Skip' : 'Include'}
                            </CButton>
                          )}
                        </div>
                      )}
                    </CTableDataCell>
                  </CTableRow>
                )
              })}
            </CTableBody>
          </CTable>
        </div>
      </CCard>

      <div className="mt-3">
        <InfoBox>
          Rows that need correction are excluded from the import until fixed. Use{' '}
          <strong>Edit</strong> to fix a row inline, or <strong>Skip</strong> to leave a valid row
          out of this import.
        </InfoBox>
      </div>

      <div className="d-flex justify-content-between mt-3">
        <CButton color="secondary" variant="outline" onClick={() => goToStep(1)}>
          <CIcon icon={cilArrowLeft} className="me-1" />
          Back to Upload
        </CButton>
        <CButton color="primary" disabled={!readyRows.length} onClick={() => goToStep(3)}>
          Continue to Review
        </CButton>
      </div>
    </>
  )

  const renderReview = () => {
    const excluded = rows.length - readyRows.length
    return (
      <>
        {importErrors.length > 0 && (
          <div className="mb-3">
            {importErrors.map((error, index) => (
              <CAlert color="danger" key={index}>
                {error}
              </CAlert>
            ))}
          </div>
        )}
        <CRow className="g-3 mb-3">
          <CCol sm={6} lg={3}>
            <StatTile label="File Rows" value={rows.length} tone="secondary" icon={cilFile} />
          </CCol>
          <CCol sm={6} lg={3}>
            <StatTile
              label="Ready to Import"
              value={readyRows.length}
              tone="success"
              icon={cilCheckCircle}
            />
          </CCol>
          <CCol sm={6} lg={3}>
            <StatTile label="Excluded" value={excluded} tone="warning" icon={cilWarning} />
          </CCol>
          <CCol sm={6} lg={3}>
            <StatTile
              label="Needs Correction"
              value={summary.needsAttention}
              tone="danger"
              icon={cilWarning}
            />
          </CCol>
        </CRow>
        <InfoBox>
          This will create {readyRows.length} new player record{readyRows.length === 1 ? '' : 's'}.
          Review the list below before importing.
        </InfoBox>
        <CCard className="border-secondary-subtle rounded-4 overflow-hidden mt-3">
          <div className="table-responsive" style={{ maxHeight: 320, overflowY: 'auto' }}>
            <CTable align="middle" hover className="mb-0 pi-import-table">
              <CTableHead style={{ backgroundColor: 'var(--cui-tertiary-bg, #f8f9fa)' }}>
                <CTableRow>
                  <CTableHeaderCell className="ps-4">First Name</CTableHeaderCell>
                  <CTableHeaderCell>Last Name</CTableHeaderCell>
                  <CTableHeaderCell>Position</CTableHeaderCell>
                  <CTableHeaderCell>Value</CTableHeaderCell>
                  <CTableHeaderCell>Team</CTableHeaderCell>
                </CTableRow>
              </CTableHead>
              <CTableBody>
                {readyRows.map((r) => (
                  <CTableRow key={r.row}>
                    <CTableDataCell className="ps-4">{r.first_name || '—'}</CTableDataCell>
                    <CTableDataCell>{r.last_name || '—'}</CTableDataCell>
                    <CTableDataCell>{r.position}</CTableDataCell>
                    <CTableDataCell>{r.value}</CTableDataCell>
                    <CTableDataCell>{r.team_name || r.team_code || '—'}</CTableDataCell>
                  </CTableRow>
                ))}
              </CTableBody>
            </CTable>
          </div>
        </CCard>
        <div className="d-flex justify-content-between mt-3">
          <CButton color="secondary" variant="outline" onClick={() => goToStep(2)}>
            <CIcon icon={cilArrowLeft} className="me-1" />
            Back
          </CButton>
          <CLoadingButton
            color="success"
            loading={importing}
            disabled={!readyRows.length}
            onClick={runImport}
          >
            <CIcon icon={cilCloudUpload} className="me-1" />
            Import {readyRows.length} Player{readyRows.length === 1 ? '' : 's'}
          </CLoadingButton>
        </div>
      </>
    )
  }

  return (
    <>
      <style>{`
        .pi-import-table > :not(caption) > * > * { padding-top: .85rem; padding-bottom: .85rem; }
      `}</style>
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div>
          <h4 className="fw-bold mb-0">Import Players</h4>
          <div className="small text-medium-emphasis">
            Create multiple player records from a spreadsheet.
          </div>
        </div>
        <CButton color="secondary" variant="outline" onClick={() => navigate('/players')}>
          <CIcon icon={cilArrowLeft} className="me-1" />
          Back to Players
        </CButton>
      </div>
      <CCard className="mb-4 border-0 shadow-sm">
        <CCardBody>
          <MiniStepper current={wizardStep} maxReached={maxWizardStep} onStepClick={goToStep} />
          {wizardStep === 1 && renderUpload()}
          {wizardStep === 2 && renderValidate()}
          {wizardStep === 3 && renderReview()}
        </CCardBody>
      </CCard>
    </>
  )
}

export default ImportPlayer
